
import { Link } from 'react-router-dom';

export default function EffectiveMedication() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Article Header */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-teal-600 font-semibold">ビースト君</p>
                <p className="text-gray-500 text-sm">2025年9月30日</p>
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">薬の効果的な飲み方とタイミング</h1>
            <p className="text-xl text-gray-600">薬の効果を最大限に引き出すための正しい服用方法について解説します</p>
          </div>
          <div className="flex justify-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/cd80a72b41b5d5eab71ae7d8c6923d14.jpg"
              alt="薬の効果的な飲み方"
              className="w-full max-w-2xl h-80 object-cover object-top rounded-xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              こんにちは、ビースト君です！今日は薬の効果的な飲み方とタイミングについて、筋トレの経験も交えながらお話しします。
              薬も筋トレと同じで、正しい方法とタイミングが効果を大きく左右するんです。
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">服用タイミングの基本</h2>
            
            <div className="bg-teal-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-teal-800 mb-4">主な服用タイミング</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">食前（食事の30分前）</h4>
                  <p className="text-gray-600 text-sm">胃酸の影響を受けやすい薬や、食事の影響で吸収が悪くなる薬</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">食後（食事の30分以内）</h4>
                  <p className="text-gray-600 text-sm">胃への刺激を和らげたい薬や、食事と一緒に摂ると吸収が良くなる薬</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">食間（食事の2時間後）</h4>
                  <p className="text-gray-600 text-sm">空腹時に効果を発揮する薬や、食事の影響を避けたい薬</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">就寝前</h4>
                  <p className="text-gray-600 text-sm">睡眠中に効果を発揮する薬や、眠気を誘う副作用がある薬</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">水で飲むことの重要性</h2>
            
            <p className="text-gray-700 mb-6">
              筋トレでも水分補給が大切なように、薬を飲むときも十分な水（コップ1杯程度）で飲むことが重要です。
              水以外の飲み物で薬を飲むと、薬の効果に影響が出ることがあります。
            </p>

            <div className="bg-red-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-red-800 mb-4">注意が必要な飲み物</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mr-2 mt-1"></i>
                  <span><strong>グレープフルーツジュース：</strong>一部の薬の代謝を阻害し、効果が強く出すぎることがある</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mr-2 mt-1"></i>
                  <span><strong>牛乳：</strong>カルシウムが薬と結合して吸収を妨げることがある</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mr-2 mt-1"></i>
                  <span><strong>お茶・コーヒー：</strong>タンニンやカフェインが薬の効果に影響することがある</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mr-2 mt-1"></i>
                  <span><strong>アルコール：</strong>薬の効果を増強したり、副作用を強めたりする危険性がある</span>
                </li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">筋トレから学ぶ継続の重要性</h2>
            
            <p className="text-gray-700 mb-6">
              筋トレは継続することで効果が現れるように、薬も決められた期間しっかりと飲み続けることが大切です。
              症状が良くなったからといって自己判断で中止すると、病気が再発したり、薬が効きにくくなったりすることがあります。
            </p>

            <div className="bg-green-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-green-800 mb-4">服薬継続のコツ</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">習慣化する</h4>
                  <p className="text-gray-600 text-sm">食事や歯磨きなど、日常の行動と組み合わせて習慣化する</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">アラームを活用</h4>
                  <p className="text-gray-600 text-sm">スマートフォンのアラームやお薬手帳アプリを活用する</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">一包化サービス</h4>
                  <p className="text-gray-600 text-sm">薬局で一回分ずつ包装してもらい、飲み忘れを防ぐ</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">家族のサポート</h4>
                  <p className="text-gray-600 text-sm">家族に服薬を見守ってもらい、お互いに声をかけ合う</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">副作用への対処法</h2>
            
            <p className="text-gray-700 mb-6">
              筋トレでも筋肉痛が起こるように、薬にも副作用があります。でも、正しい知識があれば適切に対処できます。
              気になる症状があったら、すぐに薬剤師や医師に相談しましょう。
            </p>

            <div className="bg-yellow-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-yellow-800 mb-4">よくある副作用と対処法</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">胃の不快感</h4>
                  <p className="text-gray-600 text-sm">食後に服用する、胃薬と併用する（医師・薬剤師に相談）</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">眠気</h4>
                  <p className="text-gray-600 text-sm">運転や危険な作業を避ける、服用時間を調整する</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">便秘</h4>
                  <p className="text-gray-600 text-sm">水分を多く摂る、食物繊維の多い食事を心がける</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">まとめ</h2>
            
            <p className="text-gray-700 mb-6">
              薬の効果を最大限に引き出すためには、正しい飲み方とタイミングが重要です。
              筋トレと同じように、継続と正しい方法が成功の鍵となります。
            </p>

            <div className="bg-teal-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-teal-800 mb-4">ビースト君からのアドバイス</h3>
              <p className="text-gray-700">
                薬も筋トレも、正しい知識と継続が大切です。分からないことがあったら、遠慮なく薬剤師に相談してください。
                みんなで健康な体を作っていきましょう！💪
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">関連記事</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/beast-blog/health-management" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20demonstrating%20exercise%20and%20health%20tips%2C%20cartoon%20illustration%20showing%20workout%20and%20wellness%20advice%2C%20friendly%20educational%20content&width=400&height=250&seq=beast-health-management-two-block-updated&orientation=landscape"
                alt="健康管理"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">薬剤師が教える筋トレと健康管理</h3>
                <p className="text-gray-600">薬学知識と筋トレ経験を活かした健康管理方法をお伝えします</p>
              </div>
            </Link>
            <Link to="/beast-blog/otc-selection" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20in%20pharmacy%20setting%20explaining%20OTC%20medicines%2C%20cartoon%20illustration%20with%20medicine%20shelves%20and%20friendly%20consultation%20scene&width=400&height=250&seq=beast-otc-selection-two-block-updated&orientation=landscape"
                alt="OTC医薬品"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">市販薬選びで失敗しないコツ</h3>
                <p className="text-gray-600">薬局でよく聞かれる市販薬の選び方について解説します</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">患者さん向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康サポート</Link></li>
                <li><Link to="/self-care" className="hover:text-white cursor-pointer">セルフケア</Link></li>
                <li><Link to="/otc" className="hover:text-white cursor-pointer">OTC医薬品</Link></li>
                <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
